import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import ahoy from 'ahoy.js';
import {
  ArrowRight,
  Lock,
  FileText,
  Banknote,
  ArrowRightLeft,
  TrendingUp,
  Briefcase,
  Building2,
  BarChart3,
  Users,
} from 'lucide-react';
import { formatCompact } from '../FinancingCalculator/FinancingResultsPanel';
import { calculateMonthlyPayment } from '../../Succession/Financing/financingCalculations';
import ReadyToConnectWidget from '../shared/ReadyToConnectWidget';
import FeaturedPartnerCard from '../shared/FeaturedPartnerCard';
import CashFlowProjectionModal from './CashFlowProjectionModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusDot({ variant = 'orange' }) {
  const colors = { orange: 'hsl(var(--succeedo-orange))', green: 'hsl(var(--chart-2))' };
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ backgroundColor: colors[variant] || colors.orange }}
    />
  );
}

function formatCurrency(value) {
  if (value == null) return '$0';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value) {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

function formatPercentRaw(value) {
  if (value == null) return '0%';
  return `${Number(value).toFixed(1)}%`;
}

const PROVINCES = [
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'AB', label: 'Alberta' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'QC', label: 'Quebec' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'YT', label: 'Yukon' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
];

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------
function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        checked ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-border'
      }`}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Currency input
// ---------------------------------------------------------------------------
function CurrencyInput({ value, onChange, prefix = '$', suffix, className = '' }) {
  const displayValue = value != null && value !== '' ? Number(value).toLocaleString() : '';

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || !isNaN(Number(raw))) {
      onChange(raw === '' ? '' : Number(raw));
    }
  };

  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground">{prefix}</span>
      )}
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        className={`w-full bg-background border border-input rounded-[8px] ${
          prefix ? 'pl-8' : 'px-4'
        } ${suffix ? 'pr-8' : 'pr-4'} py-3 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none ${className}`}
      />
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground">{suffix}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill button group
// ---------------------------------------------------------------------------
function PillGroup({ options, value, onChange, size = 'md' }) {
  const sizeClasses = size === 'sm' ? 'px-3 py-1.5 text-[12px]' : 'px-4 py-2 text-[13px]';
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`${sizeClasses} rounded-full transition-colors ${
            value === opt.value
              ? 'bg-foreground text-background'
              : 'border border-border text-muted-foreground hover:border-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================

const CASH_FLOW_LENGTH_OPTIONS = [
  { value: 3, label: '3 YRS' },
  { value: 5, label: '5 YRS' },
  { value: 7, label: '7 YRS' },
  { value: 10, label: '10 YRS' },
];

const DealStructureResults = ({
  result,
  inputs,
  onBack,
  onRecalculate,
  recalculating,
  onSaveSnapshot,
  onLoadSnapshot,
  onSetPrimary,
  onRequestIntroduction,
  introLoading,
  introductionRequests,
  dealStructureSnapshots,
  accountantPartners,
  lawyerPartners,
  valuationContext,
  readOnly,
  dealTeam,
  onShareWithTeam,
}) => {
  // ------ Editable input state (initialized from wizard inputs) ------
  const inp = inputs || {};
  const [askingPrice, setAskingPrice] = useState(inp.askingPrice || result?.asking_price || 750000);
  const [province, setProvince] = useState(inp.province || result?.tax?.province || 'ON');
  const [downPayment, setDownPayment] = useState(
    inp.downPayment || result?.cash_at_closing || 562500
  );
  const [repaymentTerm, setRepaymentTerm] = useState(
    inp.repaymentTermYears || result?.seller_note?.term_years || 5
  );
  const [interestRate, setInterestRate] = useState(
    inp.interestRate != null
      ? inp.interestRate
      : result?.seller_note?.rate != null
      ? result.seller_note.rate * 100
      : 5.0
  );
  const [earnoutAmount, setEarnoutAmount] = useState(
    inp.earnoutAmount || result?.earnout?.total_amount || 0
  );
  const [earnoutDuration, setEarnoutDuration] = useState(
    inp.earnoutDurationYears || result?.earnout?.duration_years || 2
  );

  // Buyer financing (client-side only)
  const [hasBuyerFinancing, setHasBuyerFinancing] = useState(false);
  const [buyerLoanAmount, setBuyerLoanAmount] = useState(downPayment);
  const [buyerInterestRate, setBuyerInterestRate] = useState(6.5);
  const [buyerLoanTerm, setBuyerLoanTerm] = useState(10);

  // Consulting agreement (server-side)
  const [hasConsulting, setHasConsulting] = useState(
    (inp.consultingAnnualFee || 0) > 0
  );
  const [consultingAnnualFee, setConsultingAnnualFee] = useState(
    inp.consultingAnnualFee || 75000
  );
  const [consultingDuration, setConsultingDuration] = useState(
    inp.consultingDurationYears || 1
  );

  // Business projections (from valuationContext or user input)
  const [annualRevenue, setAnnualRevenue] = useState(
    valuationContext?.revenue || 0
  );
  const [annualExpenses, setAnnualExpenses] = useState(
    valuationContext?.operatingExpenses || 0
  );
  const [revenueGrowthRate, setRevenueGrowthRate] = useState(
    valuationContext?.growthRate ? (valuationContext.growthRate > 1 ? valuationContext.growthRate : valuationContext.growthRate * 100) : 5
  );
  const [expenseGrowthRate, setExpenseGrowthRate] = useState(
    valuationContext?.growthRate ? (valuationContext.growthRate > 1 ? valuationContext.growthRate : valuationContext.growthRate * 100) : 5
  );
  const [cashFlowLength, setCashFlowLength] = useState(5);

  // Save state
  const [saveStatus, setSaveStatus] = useState('idle');

  // Cash flow projection modal
  const [showCashFlowModal, setShowCashFlowModal] = useState(false);

  // Debounce timer ref
  const debounceRef = useRef(null);
  const isInitialMount = useRef(true);
  const trackedRef = useRef(false);

  // Track initial view
  useEffect(() => {
    if (!trackedRef.current) {
      ahoy.track('succeedo_deal_structure_calculated');
      trackedRef.current = true;
    }
  }, []);

  // Reset save status when result changes
  useEffect(() => {
    setSaveStatus('idle');
  }, [result]);

  // Update buyer loan amount when down payment changes
  useEffect(() => {
    if (hasBuyerFinancing) {
      setBuyerLoanAmount(downPayment);
    }
  }, [downPayment, hasBuyerFinancing]);

  // ------ Derived values (computed from local state) ------
  // Seller note is always: asking price minus down payment minus earnout
  const sellerNoteAmount = Math.max(0, askingPrice - downPayment - earnoutAmount);

  // Cash at closing is always the down payment
  const cashAtClosing = downPayment;

  const totalComponents = cashAtClosing + sellerNoteAmount + earnoutAmount;
  const isBalanced = Math.abs(totalComponents - askingPrice) < 1;

  // Consulting local calculations (for left-column preview; real numbers come from backend)
  const totalConsultingIncome = hasConsulting ? consultingAnnualFee * consultingDuration : 0;

  // ------ Buyer financing (pure client-side) ------
  const buyerMonthlyPayment =
    hasBuyerFinancing && buyerLoanAmount > 0
      ? calculateMonthlyPayment(buyerLoanAmount, buyerInterestRate / 100, buyerLoanTerm * 12)
      : 0;
  const buyerTotalInterest =
    hasBuyerFinancing ? buyerMonthlyPayment * buyerLoanTerm * 12 - buyerLoanAmount : 0;
  const buyerTotalCost = hasBuyerFinancing ? buyerLoanAmount + buyerTotalInterest : 0;

  // ------ Debounced recalculation ------
  // ------ Debounced recalculation ------
  // No longer sends a structure type; the backend auto-detects from the amounts.
  const buildParams = useCallback(() => {
    return {
      askingPrice: Number(askingPrice) || 0,
      structure: 'auto',
      downPayment: Number(downPayment) || 0,
      sellerNoteAmount: sellerNoteAmount > 0 ? sellerNoteAmount : null,
      repaymentTermYears: sellerNoteAmount > 0 ? repaymentTerm : null,
      interestRate: sellerNoteAmount > 0 ? Number(interestRate) || 0 : null,
      earnoutAmount: Number(earnoutAmount) > 0 ? Number(earnoutAmount) : null,
      earnoutDurationYears: Number(earnoutAmount) > 0 ? earnoutDuration : null,
      province,
      expectedCloseMonths: inp.expectedCloseMonths || 6,
      consultingAnnualFee: hasConsulting ? Number(consultingAnnualFee) || 0 : null,
      consultingDurationYears: hasConsulting ? consultingDuration : null,
      annualRevenue: Number(annualRevenue) > 0 ? Number(annualRevenue) : null,
      annualExpenses: Number(annualExpenses) > 0 ? Number(annualExpenses) : null,
      revenueGrowthRate: Number(annualRevenue) > 0 ? Number(revenueGrowthRate) || 0 : null,
      expenseGrowthRate: Number(annualRevenue) > 0 ? Number(expenseGrowthRate) || 0 : null,
      cashFlowLength: Number(annualRevenue) > 0 ? cashFlowLength : null,
    };
  }, [
    askingPrice,
    downPayment,
    sellerNoteAmount,
    repaymentTerm,
    interestRate,
    earnoutAmount,
    earnoutDuration,
    province,
    hasConsulting,
    consultingAnnualFee,
    consultingDuration,
    inp.expectedCloseMonths,
    annualRevenue,
    annualExpenses,
    revenueGrowthRate,
    expenseGrowthRate,
    cashFlowLength,
  ]);

  useEffect(() => {
    if (!onRecalculate) return;

    // Skip the initial mount — we already have results from the wizard
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const params = buildParams();
      if (params.askingPrice > 0) {
        onRecalculate(params);
        ahoy.track('succeedo_deal_structure_calculated', {
          asking_price: params.askingPrice,
        });
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    askingPrice,
    downPayment,
    repaymentTerm,
    interestRate,
    earnoutAmount,
    earnoutDuration,
    province,
    hasConsulting,
    consultingAnnualFee,
    consultingDuration,
    annualRevenue,
    annualExpenses,
    revenueGrowthRate,
    expenseGrowthRate,
    cashFlowLength,
    buildParams,
    onRecalculate,
  ]);

  // ------ No result guard ------
  if (!result) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <p className="text-muted-foreground text-sm">No results to display.</p>
      </div>
    );
  }

  // ------ Extract result data ------
  const {
    tax,
    after_tax_proceeds,
    timeline,
    seller_note: resultNote,
    earnout: resultEarnout,
    consulting: resultConsulting,
  } = result;

  const cashPercent = askingPrice > 0 ? ((cashAtClosing / askingPrice) * 100).toFixed(1) : 0;
  const notePercent = askingPrice > 0 ? ((sellerNoteAmount / askingPrice) * 100).toFixed(1) : 0;
  const earnoutPercent =
    askingPrice > 0 ? ((earnoutAmount / askingPrice) * 100).toFixed(1) : 0;

  // Monthly note payment from result (or local calc)
  const noteMonthlyPayment = resultNote?.monthly_payment || 0;
  const noteTotalInterest = resultNote?.total_interest || 0;

  // Over-time amount
  const overTimeAmount = sellerNoteAmount + noteTotalInterest + earnoutAmount;

  // Partner intro states
  const pendingAccountantIntro = introductionRequests.find(
    (r) =>
      r.partner_category === 'accountant' &&
      (r.status === 'pending' || r.status === 'matched')
  );
  const pendingLawyerIntro = introductionRequests.find(
    (r) =>
      r.partner_category === 'lawyer' &&
      (r.status === 'pending' || r.status === 'matched')
  );
  const accountantState = pendingAccountantIntro ? 'pending' : 'generic';
  const lawyerState = pendingLawyerIntro ? 'pending' : 'generic';

  const handleSave = async () => {
    if (saveStatus === 'saving') return;
    setSaveStatus('saving');
    if (onSaveSnapshot) {
      const params = buildParams();
      const snapshot = await onSaveSnapshot({ inputs: params, results: result });
      if (snapshot) {
        setSaveStatus('saved');
      } else {
        setSaveStatus('idle');
      }
    }
  };

  const displayPartners = [...accountantPartners, ...lawyerPartners];

  // Province label for display
  const provinceObj = PROVINCES.find((p) => p.value === (tax?.province || province)) || PROVINCES[0];

  // Consulting from result
  const consultingTaxFromResult = resultConsulting?.tax || 0;
  const netConsultingFromResult = resultConsulting?.net_income || 0;

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
              DEAL STRUCTURE
            </span>
            {recalculating && (
              <span className="text-[11px] text-muted-foreground ml-2">Calculating...</span>
            )}
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onBack}
              className="text-[14px] text-muted-foreground hover:text-foreground transition-colors"
            >
              &larr; Start new scenario
            </button>
          )}
        </div>
        <h1 className="font-serif text-[42px] font-light text-foreground tracking-[-0.02em] mb-2">
          Structure Your Deal
        </h1>
        <p className="text-[16px] text-muted-foreground">
          Model how the sale could be structured — cash, seller note, earnout, or a blend.
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ================================================================ */}
        {/* LEFT COLUMN - Editable Inputs                                    */}
        {/* ================================================================ */}
        <div className="bg-card border border-border rounded-[16px] p-6">
          <div className="flex items-center gap-2 mb-6">
            <StatusDot variant="orange" />
            <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
              DEAL PARAMETERS
            </span>
          </div>

          <div className="space-y-6">
            {/* SECTION 1 -- DEAL OVERVIEW */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  DEAL OVERVIEW
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">Asking Price</label>
                  {readOnly ? (
                    <div className="text-[14px] text-foreground">{formatCurrency(askingPrice)}</div>
                  ) : (
                    <CurrencyInput value={askingPrice} onChange={setAskingPrice} />
                  )}
                </div>
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">Province (for tax)</label>
                  {readOnly ? (
                    <div className="text-[14px] text-foreground">
                      {(PROVINCES.find((p) => p.value === province) || PROVINCES[0]).label}
                    </div>
                  ) : (
                    <select
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                      className="w-full bg-background border border-input rounded-[8px] px-4 py-3 text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
                    >
                      {PROVINCES.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 2 -- CASH AT CLOSING */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Banknote className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  CASH AT CLOSING
                </span>
              </div>
              <div>
                <label className="block text-[13px] text-muted-foreground mb-2">Down Payment</label>
                {readOnly ? (
                  <div className="text-[14px] text-foreground">{formatCurrency(downPayment)}</div>
                ) : (
                  <CurrencyInput value={downPayment} onChange={setDownPayment} />
                )}
                <p className="text-[12px] text-muted-foreground mt-2">
                  {askingPrice > 0
                    ? `${((downPayment / askingPrice) * 100).toFixed(1)}% of asking price`
                    : '0% of asking price'}
                </p>
              </div>
            </div>

            {/* SECTION 3 -- SELLER NOTE */}
            <div
              className={`transition-opacity duration-300 ${
                sellerNoteAmount === 0 ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-4">
                <ArrowRightLeft className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  SELLER NOTE
                </span>
              </div>
              {sellerNoteAmount === 0 ? (
                <p className="text-[13px] text-muted-foreground/50">
                  No seller note -- full cash at closing
                </p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="w-[14px] h-[14px] text-muted-foreground" />
                      <label className="block text-[13px] text-muted-foreground">Note Amount</label>
                    </div>
                    <div className="text-[14px] text-foreground">
                      {formatCurrency(sellerNoteAmount)} (auto-calculated)
                    </div>
                  </div>

                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-3">Repayment Term</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{repaymentTerm} {repaymentTerm === 1 ? 'year' : 'years'}</div>
                    ) : (
                      <PillGroup
                        options={[
                          { value: 2, label: '2 YRS' },
                          { value: 3, label: '3 YRS' },
                          { value: 5, label: '5 YRS' },
                          { value: 7, label: '7 YRS' },
                          { value: 10, label: '10 YRS' },
                        ]}
                        value={repaymentTerm}
                        onChange={setRepaymentTerm}
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">Interest Rate</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{Number(interestRate).toFixed(1)}%</div>
                    ) : (
                      <CurrencyInput
                        value={interestRate}
                        onChange={setInterestRate}
                        prefix={null}
                        suffix="%"
                      />
                    )}
                  </div>

                  {resultNote && (
                    <p className="text-[12px] text-muted-foreground">
                      {formatCurrency(resultNote.monthly_payment)}/mo for{' '}
                      {repaymentTerm * 12} months &middot;{' '}
                      {formatCurrency(
                        sellerNoteAmount + (resultNote.total_interest || 0)
                      )}{' '}
                      total repayment
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 4 -- EARNOUT */}
            <div
              className={`transition-opacity duration-300 ${
                earnoutAmount === 0 ? 'opacity-40' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  EARNOUT
                </span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] text-muted-foreground mb-2">Earnout Amount</label>
                  {readOnly ? (
                    <div className="text-[14px] text-foreground">{formatCurrency(earnoutAmount)}</div>
                  ) : (
                    <CurrencyInput value={earnoutAmount} onChange={setEarnoutAmount} />
                  )}
                  {earnoutAmount === 0 && (
                    <p className="text-[12px] text-muted-foreground/50 mt-2">
                      Optional: Performance-based payment over time
                    </p>
                  )}
                </div>

                {earnoutAmount > 0 && (
                  <>
                    <div>
                      <label className="block text-[13px] text-muted-foreground mb-3">Duration</label>
                      {readOnly ? (
                        <div className="text-[14px] text-foreground">{earnoutDuration} {earnoutDuration === 1 ? 'year' : 'years'}</div>
                      ) : (
                        <PillGroup
                          options={[
                            { value: 1, label: '1 YR' },
                            { value: 2, label: '2 YRS' },
                            { value: 3, label: '3 YRS' },
                            { value: 5, label: '5 YRS' },
                          ]}
                          value={earnoutDuration}
                          onChange={setEarnoutDuration}
                        />
                      )}
                    </div>

                    <p className="text-[12px] text-muted-foreground">
                      {formatCurrency(earnoutAmount / earnoutDuration)}/yr over {earnoutDuration}{' '}
                      year{earnoutDuration > 1 ? 's' : ''}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* SECTION 5 -- CONSULTING AGREEMENT */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-[18px] h-[18px] text-muted-foreground" />
                  <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                    CONSULTING AGREEMENT
                  </span>
                </div>
                {readOnly ? (
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">
                    {hasConsulting ? 'INCLUDED' : 'NOT INCLUDED'}
                  </span>
                ) : (
                  <Toggle checked={hasConsulting} onChange={setHasConsulting} />
                )}
              </div>

              {hasConsulting && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">Annual Fee</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{formatCurrency(consultingAnnualFee)}</div>
                    ) : (
                      <CurrencyInput value={consultingAnnualFee} onChange={setConsultingAnnualFee} />
                    )}
                  </div>

                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-3">Duration</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{consultingDuration} {consultingDuration === 1 ? 'year' : 'years'}</div>
                    ) : (
                      <PillGroup
                        options={[
                          { value: 1, label: '1 YR' },
                          { value: 2, label: '2 YRS' },
                          { value: 3, label: '3 YRS' },
                        ]}
                        value={consultingDuration}
                        onChange={setConsultingDuration}
                        size="sm"
                      />
                    )}
                  </div>

                  <div className="space-y-1">
                    <div className="text-[14px] font-medium text-foreground">
                      Total: {formatCurrency(totalConsultingIncome)}
                    </div>
                    {resultConsulting && (
                      <>
                        <div className="text-[12px] text-muted-foreground">
                          Tax: ~{formatCurrency(resultConsulting.tax)} &middot; Net: ~
                          {formatCurrency(resultConsulting.net_income)}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="bg-muted rounded-[8px] p-3">
                    <p className="text-[12px] text-muted-foreground">
                      Consulting income is taxed as ordinary income at your full marginal rate. No
                      capital gains treatment.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 6 -- BUYER FINANCING */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-[18px] h-[18px] text-muted-foreground" />
                  <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                    BUYER'S ACQUISITION LOAN
                  </span>
                </div>
                {readOnly ? (
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">
                    {hasBuyerFinancing ? 'INCLUDED' : 'NOT INCLUDED'}
                  </span>
                ) : (
                  <Toggle checked={hasBuyerFinancing} onChange={setHasBuyerFinancing} />
                )}
              </div>

              {hasBuyerFinancing && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">Loan Amount</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{formatCurrency(buyerLoanAmount)}</div>
                    ) : (
                      <CurrencyInput value={buyerLoanAmount} onChange={setBuyerLoanAmount} />
                    )}
                    <p className="text-[12px] text-muted-foreground mt-2">
                      How the buyer finances the down payment.
                    </p>
                  </div>

                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">Interest Rate</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{Number(buyerInterestRate).toFixed(1)}%</div>
                    ) : (
                      <CurrencyInput
                        value={buyerInterestRate}
                        onChange={setBuyerInterestRate}
                        prefix={null}
                        suffix="%"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-3">Loan Term</label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{buyerLoanTerm} {buyerLoanTerm === 1 ? 'year' : 'years'}</div>
                    ) : (
                      <PillGroup
                        options={[
                          { value: 5, label: '5 YRS' },
                          { value: 7, label: '7 YRS' },
                          { value: 10, label: '10 YRS' },
                          { value: 15, label: '15 YRS' },
                        ]}
                        value={buyerLoanTerm}
                        onChange={setBuyerLoanTerm}
                        size="sm"
                      />
                    )}
                  </div>

                  <p className="text-[12px] text-muted-foreground">
                    {formatCurrency(buyerMonthlyPayment)}/mo &middot;{' '}
                    {formatCurrency(buyerTotalCost)} total cost of borrowing
                  </p>

                  {!readOnly && (
                    <a
                      href="/succeedo/financing-calculator"
                      className="text-[13px] hover:underline inline-block"
                      style={{ color: 'hsl(var(--succeedo-orange))' }}
                    >
                      Run detailed financing scenarios &rarr;
                    </a>
                  )}
                </div>
              )}
            </div>
            {/* SECTION 7 -- BUSINESS PROJECTIONS */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-[18px] h-[18px] text-muted-foreground" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  BUSINESS PROJECTIONS
                </span>
              </div>

              {valuationContext ? (
                <div
                  className="rounded-[8px] px-3 py-2 mb-4 text-[12px] text-muted-foreground"
                  style={{
                    backgroundColor: 'hsl(var(--succeedo-orange) / 0.05)',
                    border: '1px solid hsl(var(--succeedo-orange) / 0.15)',
                  }}
                >
                  Pre-filled from your{' '}
                  {(valuationContext.sectorCluster || '')
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}{' '}
                  valuation
                  {valuationContext.snapshotDate
                    ? ` of ${new Date(valuationContext.snapshotDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : ''}
                </div>
              ) : (
                !readOnly && (
                  <p className="text-[12px] text-muted-foreground/50 mb-4">
                    Add revenue and expense data to project buyer cash flows.{' '}
                    <a
                      href="/succeedo/valuation"
                      className="text-[hsl(var(--succeedo-orange))] hover:underline"
                    >
                      Run a valuation first &rarr;
                    </a>
                  </p>
                )
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Annual Revenue
                    </label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{formatCurrency(annualRevenue)}</div>
                    ) : (
                      <CurrencyInput value={annualRevenue} onChange={setAnnualRevenue} />
                    )}
                  </div>
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Annual Expenses
                    </label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{formatCurrency(annualExpenses)}</div>
                    ) : (
                      <CurrencyInput value={annualExpenses} onChange={setAnnualExpenses} />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Revenue Growth Rate
                    </label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{Number(revenueGrowthRate).toFixed(1)}%</div>
                    ) : (
                      <CurrencyInput
                        value={revenueGrowthRate}
                        onChange={setRevenueGrowthRate}
                        prefix={null}
                        suffix="%"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-[13px] text-muted-foreground mb-2">
                      Expense Growth Rate
                    </label>
                    {readOnly ? (
                      <div className="text-[14px] text-foreground">{Number(expenseGrowthRate).toFixed(1)}%</div>
                    ) : (
                      <CurrencyInput
                        value={expenseGrowthRate}
                        onChange={setExpenseGrowthRate}
                        prefix={null}
                        suffix="%"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[13px] text-muted-foreground mb-3">
                    Cash Flow Length
                  </label>
                  {readOnly ? (
                    <div className="text-[14px] text-foreground">
                      {(CASH_FLOW_LENGTH_OPTIONS.find((o) => o.value === cashFlowLength) || { label: `${cashFlowLength} YRS` }).label}
                    </div>
                  ) : (
                    <PillGroup
                      options={CASH_FLOW_LENGTH_OPTIONS}
                      value={cashFlowLength}
                      onChange={setCashFlowLength}
                      size="sm"
                    />
                  )}
                </div>

                {Number(annualRevenue) > 0 && Number(annualExpenses) > 0 && (
                  <p className="text-[12px] text-muted-foreground">
                    EBITDA: {formatCurrency(Number(annualRevenue) - Number(annualExpenses))} &middot;{' '}
                    Margin: {((1 - Number(annualExpenses) / Number(annualRevenue)) * 100).toFixed(1)}%
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* BALANCE SUMMARY */}
          <div className="border-t border-border pt-4 mt-6">
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <div className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Cash</div>
                <div
                  className={`text-[14px] font-medium ${
                    cashAtClosing > 0
                      ? 'text-[hsl(var(--chart-2))]'
                      : 'text-muted-foreground'
                  }`}
                >
                  {formatCurrency(cashAtClosing)}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Note</div>
                <div
                  className={`text-[14px] font-medium ${
                    sellerNoteAmount > 0 ? '' : 'text-muted-foreground'
                  }`}
                  style={
                    sellerNoteAmount > 0
                      ? { color: 'hsl(var(--succeedo-orange))' }
                      : undefined
                  }
                >
                  {formatCurrency(sellerNoteAmount)}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-muted-foreground mb-1">Earnout</div>
                <div
                  className={`text-[14px] font-medium ${
                    earnoutAmount > 0
                      ? 'text-[hsl(var(--chart-4))]'
                      : 'text-muted-foreground'
                  }`}
                >
                  {formatCurrency(earnoutAmount)}
                </div>
              </div>
            </div>
            <div className="text-[14px] font-semibold text-foreground mb-2">
              Total: {formatCurrency(totalComponents)}
            </div>
            {!isBalanced && (
              <p className="text-[12px] text-destructive flex items-center gap-1">
                <span>&#9888;</span> Components don't sum to asking price
              </p>
            )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* RIGHT COLUMN - Live Results                                      */}
        {/* ================================================================ */}
        <div className="space-y-6">
          {/* Hero Card - After-Tax Proceeds */}
          <div className="bg-card border border-border rounded-[16px] p-6">
            <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">
              AFTER-TAX PROCEEDS
            </div>
            <div
              className="font-serif text-[48px] font-light mb-2"
              style={{ color: 'hsl(var(--chart-2))' }}
            >
              {formatCurrency(after_tax_proceeds)}
            </div>
            <p className="text-[14px] text-muted-foreground">
              of {formatCurrency(askingPrice)} asking price &middot;{' '}
              {formatPercent(tax?.effective_tax_rate)} effective tax rate
            </p>
            {resultConsulting && (
              <p className="text-[12px] text-muted-foreground mt-2">
                Sale proceeds:{' '}
                {formatCurrency(
                  after_tax_proceeds - netConsultingFromResult
                )}{' '}
                + Consulting: {formatCurrency(netConsultingFromResult)} ={' '}
                {formatCurrency(after_tax_proceeds)} total
              </p>
            )}
            {(sellerNoteAmount > 0 || earnoutAmount > 0) && (
              <p className="text-[12px] text-muted-foreground mt-3">
                Total nominal proceeds over time (not discounted for time value of money)
              </p>
            )}
          </div>

          {/* Buyer's Financing Burden */}
          {hasBuyerFinancing && (
            <div className="bg-muted/30 border border-border rounded-[12px] p-4">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-4">
                BUYER'S FINANCING BURDEN
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-[16px] font-medium text-foreground">
                    {formatCurrency(buyerMonthlyPayment)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">Monthly Payment</div>
                </div>
                <div>
                  <div className="text-[14px] text-muted-foreground">
                    {formatCurrency(buyerTotalInterest)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">Total Interest</div>
                </div>
                <div>
                  <div className="text-[14px] text-muted-foreground">
                    {formatCurrency(buyerTotalCost)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">Total Cost</div>
                </div>
              </div>
              <p className="text-[12px] text-muted-foreground mt-3">
                This doesn't change your proceeds -- it shows deal feasibility for the buyer.
              </p>
            </div>
          )}

          {/* Two stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-[16px] p-6">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">
                YOU RECEIVE AT CLOSING
              </div>
              <div className="font-serif text-[28px] font-light text-foreground mb-2">
                {formatCurrency(cashAtClosing)}
              </div>
              <p className="text-[13px] text-muted-foreground">
                {formatPercentRaw(cashPercent)} of asking price
              </p>
            </div>

            <div className="bg-card border border-border rounded-[16px] p-6">
              <div className="font-mono text-[11px] uppercase text-muted-foreground mb-3">
                OVER TIME
              </div>
              <div className="font-serif text-[28px] font-light text-foreground mb-2">
                {formatCurrency(overTimeAmount + totalConsultingIncome)}
              </div>
              {noteMonthlyPayment > 0 && (
                <p className="text-[13px] text-muted-foreground">
                  {formatCurrency(noteMonthlyPayment)}/mo note payments
                </p>
              )}
            </div>
          </div>

          {/* Tax Breakdown Card */}
          {tax && (
            <div className="bg-card border border-border rounded-[16px] p-6">
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  TAX BREAKDOWN
                </span>
              </div>

              {/* Province + Marginal Rate Badge */}
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="font-mono text-[10px] uppercase rounded-full px-3 py-1"
                  style={{
                    backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)',
                    color: 'hsl(var(--succeedo-orange))',
                    border: '1px solid hsl(var(--succeedo-orange) / 0.2)',
                  }}
                >
                  {provinceObj.label}
                </span>
                <span className="text-[13px] text-muted-foreground">
                  Marginal rate: {formatPercent(tax.marginal_rate)}
                </span>
              </div>

              <div className="space-y-3">
                {/* Capital Gains */}
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[14px] text-foreground">Capital Gains Tax</div>
                    <div className="text-[12px] text-muted-foreground">
                      50%/66.7% blended inclusion
                      {tax.lcge_applied > 0 &&
                        ` \u00b7 ${formatCurrency(tax.lcge_applied)} LCGE sheltered`}
                    </div>
                  </div>
                  <div className="text-[14px] text-foreground font-medium">
                    {formatCurrency(tax.capital_gains_tax)}
                  </div>
                </div>

                {/* Interest Income Tax */}
                {tax.interest_income_tax > 0 && (
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-[14px] text-foreground">Interest Income Tax</div>
                      <div className="text-[12px] text-muted-foreground">
                        Seller note interest taxed as ordinary income
                      </div>
                    </div>
                    <div className="text-[14px] text-foreground font-medium">
                      {formatCurrency(tax.interest_income_tax)}
                    </div>
                  </div>
                )}

                {/* Consulting Income Tax */}
                {tax.consulting_income_tax > 0 && (
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-[14px] text-foreground">Consulting Income Tax</div>
                      <div className="text-[12px] text-muted-foreground">
                        Ordinary income at full marginal rate
                      </div>
                    </div>
                    <div className="text-[14px] text-foreground font-medium">
                      {formatCurrency(tax.consulting_income_tax)}
                    </div>
                  </div>
                )}

                {/* Earnout Tax (shown when earnout generates tax; earnout tax is part of capital gains in current model) */}

                {/* Total Tax */}
                <div className="pt-3 border-t border-border flex justify-between items-center">
                  <div className="text-[14px] text-foreground font-semibold">Total Tax</div>
                  <div className="text-[14px] text-foreground font-semibold">
                    {formatCurrency(tax.total_tax)}
                  </div>
                </div>

                {/* Effective Rate */}
                <div className="flex justify-between items-center">
                  <div className="text-[13px] text-muted-foreground">Effective tax rate</div>
                  <div className="text-[13px] text-muted-foreground">
                    {formatPercent(tax.effective_tax_rate)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Disclaimer for non-cash deals */}
          {(sellerNoteAmount > 0 || earnoutAmount > 0) && (
            <div
              className="rounded-[12px] p-4"
              style={{
                backgroundColor: 'hsl(var(--succeedo-orange) / 0.05)',
                border: '1px solid hsl(var(--succeedo-orange) / 0.15)',
              }}
            >
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Note:</strong> With seller financing, a
                portion of capital gains tax may be deferrable over up to 5 years via the{' '}
                <strong className="text-foreground">Capital Gains Reserve</strong> -- reducing
                your Year 1 tax burden. Interest income is taxed as received. This estimate shows
                total tax liability; actual timing may vary.{' '}
                <strong className="text-foreground">Consult your accountant</strong> for tax
                planning specific to your situation.
              </p>
            </div>
          )}

          {/* Payment Timeline */}
          {timeline && timeline.length > 1 && (
            <div className="bg-card border border-border rounded-[16px] p-6">
              <div className="flex items-center gap-2 mb-8">
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  PAYMENT TIMELINE
                </span>
              </div>

              <div className="flex items-start justify-between relative">
                {/* Connecting line */}
                <div
                  className="absolute top-5 h-px bg-border"
                  style={{ left: '20px', right: '20px' }}
                />

                {timeline.map((milestone, index) => (
                  <div
                    key={milestone.period}
                    className="flex flex-col items-center relative z-10 flex-1"
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                        index === 0
                          ? 'border-2 border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/10'
                          : 'border border-border bg-muted'
                      }`}
                    >
                      {index === 0 && (
                        <div className="w-3 h-3 rounded-full bg-[hsl(var(--succeedo-orange))]" />
                      )}
                    </div>
                    <div className="font-mono text-[11px] uppercase text-muted-foreground mb-2">
                      {milestone.period}
                    </div>
                    <div className="text-[14px] text-foreground font-medium">
                      {formatCompact(milestone.cumulative)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Cash Flow Projections trigger */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => setShowCashFlowModal(true)}
                  className="text-[13px] font-medium hover:underline"
                  style={{ color: 'hsl(var(--succeedo-orange))' }}
                >
                  View Cash Flow Projections &rarr;
                </button>
              </div>
            </div>
          )}

          {/* Structure Breakdown (Stacked Bar) */}
          <div className="bg-card border border-border rounded-[16px] p-6">
            <div className="flex items-center gap-2 mb-6">
              <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                STRUCTURE BREAKDOWN
              </span>
            </div>

            {(() => {
              const totalForBar = askingPrice + totalConsultingIncome;
              const cashW = totalForBar > 0 ? (cashAtClosing / totalForBar) * 100 : 0;
              const noteW = totalForBar > 0 ? (sellerNoteAmount / totalForBar) * 100 : 0;
              const earnoutW = totalForBar > 0 ? (earnoutAmount / totalForBar) * 100 : 0;
              const consultW =
                totalForBar > 0 ? (totalConsultingIncome / totalForBar) * 100 : 0;

              return (
                <>
                  <div className="h-10 rounded-full overflow-hidden flex mb-4">
                    {cashW > 0 && (
                      <div
                        className="bg-[hsl(var(--succeedo-orange))] flex items-center justify-center"
                        style={{ width: `${cashW}%` }}
                      >
                        {cashW >= 10 && (
                          <span className="font-mono text-[11px] text-white font-medium">
                            {formatPercentRaw(cashW)}
                          </span>
                        )}
                      </div>
                    )}
                    {noteW > 0 && (
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: `${noteW}%`,
                          backgroundColor: 'hsl(var(--chart-2))',
                        }}
                      >
                        {noteW >= 10 && (
                          <span className="font-mono text-[11px] text-white font-medium">
                            {formatPercentRaw(noteW)}
                          </span>
                        )}
                      </div>
                    )}
                    {earnoutW > 0 && (
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: `${earnoutW}%`,
                          backgroundColor: 'hsl(var(--chart-4))',
                        }}
                      >
                        {earnoutW >= 10 && (
                          <span className="font-mono text-[11px] text-white font-medium">
                            {formatPercentRaw(earnoutW)}
                          </span>
                        )}
                      </div>
                    )}
                    {consultW > 0 && (
                      <div
                        className="flex items-center justify-center"
                        style={{
                          width: `${consultW}%`,
                          backgroundColor: 'hsl(var(--chart-3))',
                        }}
                      >
                        {consultW >= 10 && (
                          <span className="font-mono text-[11px] text-white font-medium">
                            {formatPercentRaw(consultW)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-6 flex-wrap">
                    {cashW > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-sm bg-[hsl(var(--succeedo-orange))]" />
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">
                          CASH AT CLOSING
                        </span>
                      </div>
                    )}
                    {noteW > 0 && (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: 'hsl(var(--chart-2))' }}
                        />
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">
                          SELLER NOTE
                        </span>
                      </div>
                    )}
                    {earnoutW > 0 && (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: 'hsl(var(--chart-4))' }}
                        />
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">
                          EARNOUT
                        </span>
                      </div>
                    )}
                    {consultW > 0 && (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: 'hsl(var(--chart-3))' }}
                        />
                        <span className="font-mono text-[10px] uppercase text-muted-foreground">
                          CONSULTING
                        </span>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Save + Snapshots */}
          {!readOnly && (
          <div className="bg-card border border-border rounded-[16px] p-6">
            <div className="flex justify-center gap-3 mb-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className={`rounded-full px-6 py-3 font-medium transition-colors ${
                  saveStatus === 'saved'
                    ? 'bg-[hsl(var(--chart-2))] text-white'
                    : 'border border-border text-foreground hover:bg-accent'
                }`}
              >
                {saveStatus === 'saved'
                  ? 'Saved \u2713'
                  : saveStatus === 'saving'
                  ? 'Saving\u2026'
                  : 'Save This Scenario \u2192'}
              </button>

              {dealTeam && onShareWithTeam && (
                <button
                  type="button"
                  onClick={onShareWithTeam}
                  aria-label={`Share this scenario with ${dealTeam.name || 'your deal team'}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))] hover:bg-[hsl(var(--succeedo-orange))]/10 transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                  Share with team
                </button>
              )}
            </div>

            {dealStructureSnapshots.length > 0 && (
              <div className="border-t border-border pt-6">
                <div className="font-mono text-[11px] uppercase text-muted-foreground mb-4">
                  SAVED SCENARIOS
                </div>
                <div className="space-y-2">
                  {dealStructureSnapshots.slice(0, 5).map((snap) => {
                    const date = snap.created_at
                      ? new Date(snap.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : '';
                    const structureLabel = (snap.structure_type || '')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase());
                    const afterTax = snap.results?.after_tax_proceeds;
                    const isPrimary = snap.is_primary;

                    return (
                      <div
                        key={snap.id}
                        className="flex items-center justify-between bg-muted border border-border rounded-[12px] px-4 py-3 hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="text-[13px] text-muted-foreground">{date}</span>
                          <span className="font-mono text-[10px] uppercase text-muted-foreground border border-border rounded-full px-2 py-0.5">
                            {structureLabel}
                          </span>
                          {afterTax && (
                            <span className="text-[14px] font-semibold text-foreground">
                              {formatCompact(afterTax)} after tax
                            </span>
                          )}
                          {isPrimary && (
                            <span
                              className="font-mono text-[10px] uppercase rounded-full px-2 py-0.5"
                              style={{
                                backgroundColor: 'hsl(var(--succeedo-orange) / 0.1)',
                                color: 'hsl(var(--succeedo-orange))',
                                border: '1px solid hsl(var(--succeedo-orange) / 0.2)',
                              }}
                            >
                              &#9733; Active
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {!isPrimary && onSetPrimary && (
                            <button
                              type="button"
                              onClick={() => onSetPrimary(snap.id)}
                              className="text-[12px] hover:underline transition-colors"
                              style={{ color: 'hsl(var(--succeedo-orange))' }}
                            >
                              Set as active &rarr;
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onLoadSnapshot && onLoadSnapshot(snap)}
                            className="text-[13px] font-medium hover:underline"
                            style={{ color: 'hsl(var(--succeedo-orange))' }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Partner CTAs */}
          {!readOnly && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReadyToConnectWidget
                partnerCategory="accountant"
                state={accountantState}
                onRequestIntroduction={() =>
                  onRequestIntroduction && onRequestIntroduction('accountant')
                }
                loading={introLoading}
                requestedTime={
                  pendingAccountantIntro?.created_at
                    ? new Date(pendingAccountantIntro.created_at).toLocaleDateString()
                    : undefined
                }
              />
              <ReadyToConnectWidget
                partnerCategory="lawyer"
                state={lawyerState}
                onRequestIntroduction={() =>
                  onRequestIntroduction && onRequestIntroduction('lawyer')
                }
                loading={introLoading}
                requestedTime={
                  pendingLawyerIntro?.created_at
                    ? new Date(pendingLawyerIntro.created_at).toLocaleDateString()
                    : undefined
                }
              />
            </div>
          )}

          {/* Featured Partners */}
          {!readOnly && displayPartners.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <StatusDot variant="orange" />
                <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
                  FEATURED PARTNERS
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {displayPartners.map((partner) => (
                  <FeaturedPartnerCard key={partner.id} partner={partner} />
                ))}
              </div>
            </div>
          )}

          {/* Next Step: Financing Calculator */}
          {!readOnly && (
            <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-5">
              <div className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-2">
                NEXT STEP
              </div>
              <h3 className="text-[16px] font-semibold text-foreground mb-2">
                Model the buyer's financing
              </h3>
              <p className="text-[14px] text-muted-foreground mb-4">
                {resultNote
                  ? `Your deal includes a seller note of ${formatCompact(sellerNoteAmount)}. See what the buyer's bank financing could look like for the remaining amount.`
                  : `Even in a full cash deal, buyers often need bank financing. Share our financing calculator to help them understand their options.`}
              </p>
              <a
                href="/succeedo/financing-calculator"
                className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-2.5 text-[14px] font-medium hover:bg-foreground/90 transition-colors"
              >
                Run Financing Scenarios <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Cash Flow Projection Modal */}
      {showCashFlowModal && (
        <CashFlowProjectionModal
          result={result}
          inputs={buildParams()}
          onClose={() => setShowCashFlowModal(false)}
          hasBuyerFinancing={hasBuyerFinancing}
          buyerFinancingData={
            hasBuyerFinancing
              ? {
                  loanAmount: buyerLoanAmount,
                  rate: buyerInterestRate,
                  termYears: buyerLoanTerm,
                  monthlyPayment: buyerMonthlyPayment,
                  totalInterest: buyerTotalInterest,
                  totalCost: buyerTotalCost,
                }
              : null
          }
        />
      )}
    </div>
  );
};

DealStructureResults.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  inputs: PropTypes.object,
  onBack: PropTypes.func,
  onRecalculate: PropTypes.func,
  recalculating: PropTypes.bool,
  onSaveSnapshot: PropTypes.func,
  onLoadSnapshot: PropTypes.func,
  onSetPrimary: PropTypes.func,
  onRequestIntroduction: PropTypes.func,
  introLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  introductionRequests: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  dealStructureSnapshots: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  accountantPartners: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  lawyerPartners: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  valuationContext: PropTypes.object,
  readOnly: PropTypes.bool,
  dealTeam: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
  }),
  onShareWithTeam: PropTypes.func,
};

DealStructureResults.defaultProps = {
  result: null,
  inputs: {},
  onBack: undefined,
  onRecalculate: undefined,
  recalculating: false,
  onSaveSnapshot: undefined,
  onLoadSnapshot: undefined,
  onSetPrimary: undefined,
  onRequestIntroduction: undefined,
  introLoading: false,
  introductionRequests: [],
  dealStructureSnapshots: [],
  accountantPartners: [],
  lawyerPartners: [],
  valuationContext: null,
  readOnly: false,
  dealTeam: null,
  onShareWithTeam: undefined,
};

export default DealStructureResults;
